import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import StockTransactions from "./StockTransactions";
import { confirmDelete } from "../../components/confirmDelete";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import Loading from "../../components/loading";
import { ArrowLeft } from "lucide-react";
import { deleteItem, getItemById } from "../../services/services";
import moment from "moment";
import Batch from "./Batch";

const ItemDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTx, setLoadingTx] = useState(false);
  const [activeTab, setActiveTab] = useState("stock");

  const selectedBusiness = JSON.parse(localStorage.getItem("selectedBusiness"));
  const dbName = selectedBusiness?.tenantDB;

  // Fetch fresh product data using getItemById
  const fetchProductDetails = async () => {
    if (!id) {
      toast.error("Invalid product ID");
      navigate("/items");
      return;
    }

    setLoading(true);
    try {
      const item = await getItemById(id);

      if (item?.data?.status === true && item?.data?.response) {
        const prod = item?.data?.response;

        const totalRemainingQty = (prod.batches || []).reduce(
          (sum, b) => sum + (Number(b.remainingQty) || 0),
          0,
        );

        const latestBatch = (prod.batches || [])
          .filter((b) => b.purchasePrice > 0)
          .sort(
            (a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate),
          )[0];

        setProduct({
          ...prod,
          totalRemainingQty,
          latestPurchasePrice: latestBatch ? latestBatch.purchasePrice : 0,
          expiryDate: latestBatch ? latestBatch.expiryDate : null,
        });
      } else {
        toast.error("Product not found");
        navigate("/items");
      }
    } catch (err) {
      console.error("Fetch product details error:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to load product details",
      );
      navigate("/items");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when component mounts or id changes
  useEffect(() => {
    fetchProductDetails();
  }, [id]);

  const handleEdit = () => {
    if (!product) return;
    navigate("/items/add", {
      state: {
        product,
        isEditMode: true,
        isPurchase: false,
      },
    });
  };

  const handleDelete = async () => {
    if (!product?._id) return;

    const confirmed = await confirmDelete(product.name);
    if (!confirmed) return;

    try {
      await deleteItem(product._id);
      toast.success("Item deleted successfully");
      navigate("/items");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to delete item");
    }
  };

  // Show loading while fetching
  if (loading) {
    return (
      <div
        id="main"
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "70vh" }}
      >
        <Loading />
      </div>
    );
  }

  // Safety: if somehow product is null after loading
  if (!product) {
    return null;
  }

  return (
    <div id="main">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="d-flex gap-3 align-items-center">
          <span
            onClick={() => navigate("/items")}
            style={{ cursor: "pointer" }}
          >
            <ArrowLeft size={24} />
          </span>
          <h4 className="mb-0 custom-title">Item Details</h4>
        </div>

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleEdit}
          >
            Edit
          </button>

          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="row g-4">
        {/* Left - Product Information */}
        <div className="col-lg-5 col-xl-4">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              {/* Your existing detail fields - they will now work because product is properly set */}
              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">Name</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0">{product.name}</p>
                  </div>
                </div>
              </div>

              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">Product Code</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0">
                      {product.productCode || "—"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">Category</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0">
                      {product.category?.name || "—"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">Unit</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0">
                      {product.unit || "—"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">HSN Code</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0">
                      {product.hsnCode || "—"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">Location</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0">
                      {product.location || "—"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">Min Stock Alert</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0">
                      {product.minStockAlert ?? 0} {product.unit?.name || ""}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">Current Stock</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0">
                      {product.totalRemainingQty ?? 0}{" "}
                      {product.unit?.name || ""}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">
                      Latest Purchase Price
                    </label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0">
                      ₹ {Number(product.latestPurchasePrice || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">Sale Price</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0">
                      ₹ {Number(product.salePrice || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">Tax Type</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0 text-capitalize">
                      {product.taxType}{" "}
                      {product.gstRate > 0 ? `(${product.gstRate}%)` : ""}
                    </p>
                  </div>
                </div>
              </div>
              {/* <div className="px-0 py-2 border-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <label className="text-muted small">Expiry Date</label>
                  </div>
                  <div className="col-7">
                    <p className="small fw-semibold mb-0 text-capitalize">
                      {product.expiryDate
                        ? moment(product.expiryDate).format("DD-MMM-YYYY")
                        : "—"}
                    </p>
                  </div>
                </div>
              </div> */}

              {product.image && (
                <div className="px-0 py-3 border-0">
                  <label className="text-muted small d-block mb-2">
                    Product Image
                  </label>
                  <img
                    src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${dbName}/product_images/${product.image}`}
                    alt={product.name}
                    className="img-fluid rounded shadow-sm"
                    style={{ maxHeight: "300px", objectFit: "contain" }}
                  />
                </div>
              )}

              {product.description && (
                <div className="px-0 py-3 border-0">
                  <label className="text-muted small d-block mb-2">
                    Description
                  </label>
                  <p
                    className="mb-0"
                    style={{ fontSize: "14px", whiteSpace: "pre-wrap" }}
                  >
                    {product.description}
                  </p>
                </div>
              )}

              {/* Custom Meta Fields */}
              {Array.isArray(product.meta) && product.meta.length > 0 && (
                <div className="px-0 py-3 border-0">
                  <label className="text-muted small d-block mb-3">
                    Additional Information
                  </label>

                  {product.meta.map((item, index) => (
                    <div
                      key={item._id || index}
                      className="row align-items-center mb-3"
                    >
                      <div className="col-5">
                        <label className="text-muted small mb-0">
                          {item.key}
                        </label>
                      </div>

                      <div className="col-7">
                        <p className="small fw-semibold mb-0">
                          {item.value || "—"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Stock Transactions */}
        <div className="col-lg-7 col-xl-8">
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "stock" ? "active" : ""}`}
                onClick={() => setActiveTab("stock")}
              >
                Stock Transactions
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "batch" ? "active" : ""}`}
                onClick={() => setActiveTab("batch")}
              >
                Batches
              </button>
            </li>
          </ul>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === "stock" && (
              <div className="tab-pane fade show active">
                <StockTransactions
                  transactions={transactions}
                  isLoading={loadingTx}
                  selectedProduct={product}
                />
              </div>
            )}
            {activeTab === "batch" && (
              <div className="tab-pane fade show mt-4 active">
                <Batch product={product} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default headerLayoutWrapper(ItemDetails);
